import { PortalDatePipe } from '../../core/pipes/portal-date.pipe';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from 'shared-library';
import { ButtonModule } from 'primeng/button';
import { Popover, PopoverModule } from 'primeng/popover';
import { PortalBreadcrumbService } from '../../core/services/portal-breadcrumb.service';
import { PortalNotification } from '../../core/services/portal-notification.service';
import { PortalProfileService } from '../../core/services/portal-profile.service';
import { PortalNotificationService } from '../../core/services/portal-notification.service';
import { PortalTranslationService } from '../../core/services/portal-translation.service';
import { PortalThemeService } from '../../core/services/portal-theme.service';

@Component({
  selector: 'investigator-shell-bar',
  standalone: true,
  imports: [PortalDatePipe, CommonModule, RouterLink, ButtonModule, PopoverModule],
  templateUrl: './investigator-shell-bar.component.html',
  styleUrl: './investigator-shell-bar.component.scss',
})
export class InvestigatorShellBarComponent implements OnInit, OnDestroy {
  readonly authService = inject(AuthService);
  readonly translation = inject(PortalTranslationService);
  readonly theme = inject(PortalThemeService);
  readonly notifications = inject(PortalNotificationService);
  readonly profile = inject(PortalProfileService);
  readonly breadcrumbs = inject(PortalBreadcrumbService);
  private readonly router = inject(Router);

  readonly badgeText = computed(() => {
    const count = this.notifications.items().length;
    return count > 9 ? '9+' : String(count);
  });
  readonly bellLabel = computed(() => {
    const count = this.notifications.items().length;
    return count ? this.text(`Notifications, ${count} unread`, `Ειδοποιήσεις, ${count} μη αναγνωσμένες`) : this.text('Notifications', 'Ειδοποιήσεις');
  });

  ngOnInit(): void {
    this.notifications.connect();
    this.profile.load();
  }

  ngOnDestroy(): void {
    this.notifications.disconnect();
  }

  text(en: string, el: string): string {
    return this.translation.currentLanguage() === 'en' ? en : el;
  }

  /** What each kind of notification says, before and after the assignment code (English, Greek). */
  private static readonly KINDS: Record<string, { icon: string; en: [string, string]; el: [string, string] }> = {
    AssignmentAssigned: { icon: 'pi-briefcase', en: ['Assignment ', ' was assigned to you'], el: ['Σας ανατέθηκε η εντολή ', ''] },
    AssignmentInProgress: { icon: 'pi-play', en: ['Work started on ', ''], el: ['Ξεκίνησε η εργασία στην εντολή ', ''] },
    AssignmentSubmitted: { icon: 'pi-send', en: ['Assignment ', ' was submitted'], el: ['Υποβλήθηκε η εντολή ', ''] },
    AssignmentClarificationRequested: { icon: 'pi-question-circle', en: ['Clarifications requested for ', ''], el: ['Ζητήθηκαν διευκρινίσεις για την εντολή ', ''] },
    AssignmentCancelled: { icon: 'pi-ban', en: ['Assignment ', ' was cancelled'], el: ['Ακυρώθηκε η εντολή ', ''] },
    AssignmentUpdated: { icon: 'pi-pencil', en: ['Assignment ', ' was updated'], el: ['Τροποποιήθηκε η εντολή ', ''] },
  };

  /** Icon and text of a notification: from its type when the backend sends it, otherwise from the English message. */
  view(item: PortalNotification): { icon: string; parts: { text: string; code: boolean }[] } {
    const kind = item.notificationType ? InvestigatorShellBarComponent.KINDS[item.notificationType] : undefined;
    if (kind && item.assignmentCode) {
      const [before, after] = this.translation.currentLanguage() === 'el' ? kind.el : kind.en;
      return {
        icon: kind.icon,
        parts: [{ text: before, code: false }, { text: item.assignmentCode, code: true }, { text: after, code: false }].filter((part) => part.text),
      };
    }
    const message = item.message ?? '';
    const icon = /clarification/i.test(message) ? 'pi-question-circle'
      : /cancel/i.test(message) ? 'pi-ban'
      : /submitted/i.test(message) ? 'pi-send'
      : /started working/i.test(message) ? 'pi-play'
      : 'pi-briefcase';
    const parts = message.split(/([A-Z]{2,6}-\d+)/).filter(Boolean).map((text) => ({ text, code: /^[A-Z]{2,6}-\d+$/.test(text) }));
    return { icon, parts };
  }

  /** Opens the assignment the notification is about, and marks it as read. */
  openNotification(item: PortalNotification, panel: Popover): void {
    if (!item.assignmentId) return;
    panel.hide();
    this.notifications.markAsRead([item.id]);
    this.router.navigate(['/queue', item.assignmentId]);
  }

  trackById(_: number, item: { id: string }): string {
    return item.id;
  }

  openQueue(panel: Popover): void {
    panel.hide();
    this.router.navigate(['/queue']);
  }

  get displayName(): string {
    const profile = this.profile.profile();
    if (profile) return `${profile.firstName} ${profile.lastName}`.trim();
    const user = this.authService.currentUserValue;
    return user?.fullName || user?.firstName + ' ' + user?.lastName || user?.name || user?.email || 'Investigator';
  }

  get initials(): string {
    return this.displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part: string) => part.charAt(0).toUpperCase())
      .join('');
  }

  logout(): void {
    this.authService.logout();
  }

  toggleLanguage(): void {
    this.translation.toggle();
  }

  toggleTheme(): void {
    this.theme.toggle();
  }

  openSettings(): void {
    this.router.navigate(['/settings']);
  }
}
